import React from 'react'

import API from 'common/src/api'
import coreUtils from 'common/src/utils/coreUtils'
import { WizardCloseButton } from 'business/direct_quote_request/components/StepHeader'

const CODE_STAGE = 'code'
const PHONE_STAGE = 'phone'
const RESEND_COOLDOWN_SECONDS = 30
const SETUP_FAIL_PHONE_ERROR =
  'Sorry, we could not send a verification code. Please check your mobile number and try again.'

const DangerAlert = ({ message }) => (
  <div className="w-full p-3 bg-base-red-50 rounded-5 flex items-center gap-2 self-stretch">
    <div className="flex-1 flex justify-start items-center gap-2">
      <hero-icon-outline name="exclamation-circle" class="h-6 w-6 text-base-red-900 flex-shrink-0" />
      <p className="text-sm font-medium text-base-red-900">{message}</p>
    </div>
  </div>
)

export default class PhoneOtpVerification extends React.Component {
  constructor(props) {
    super(props)

    this._isMounted = false

    this.state = {
      stage: CODE_STAGE,
      loading: false,
      awaitingSetup: true,
      phoneNumber: props.phoneNumber || '',
      newPhoneNumber: props.phoneNumber || '',
      phoneError: '',
      code: '',
      submitEnabled: false,
      verificationStatus: '',
      maxCodeLength: 4,
      resendCooldownUntil: null,
      resendCooldownSecondsRemaining: 0,
    }
  }

  componentDidMount() {
    this._isMounted = true
    this.loadPhoneNumber()
    if (this.props.active) {
      this.initialiseActiveStage()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    this.clearResendCooldownTimer()
  }

  setStateIfMounted = (state, callback) => {
    if (!this._isMounted) return

    this.setState(state, callback)
  }

  initialiseActiveStage = () => {
    if (this.props.openChangePhone) {
      this.openChangePhoneStage(this.props.changePhoneError)
      return
    }

    if (this.props.initialSetupComplete) {
      this.setStateIfMounted({
        awaitingSetup: false,
        loading: false,
        verificationStatus: this.props.initialVerificationStatus || '',
      })
      return
    }

    this.setupPhoneVerification()
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.active && this.props.active) {
      this.initialiseActiveStage()
    }

    if (!prevProps.openChangePhone && this.props.openChangePhone) {
      this.openChangePhoneStage(this.props.changePhoneError)
    }

    if (prevProps.changePhoneError !== this.props.changePhoneError && this.props.changePhoneError) {
      this.setState({ phoneError: this.props.changePhoneError })
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.phoneNumber && nextProps.phoneNumber !== this.props.phoneNumber) {
      this.setState({
        phoneNumber: nextProps.phoneNumber,
        newPhoneNumber: nextProps.phoneNumber,
      })
    }
  }

  clearResendCooldownTimer = () => {
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer)
      this.resendCooldownTimer = null
    }
  }

  isResendOnCooldown = () => {
    const { resendCooldownUntil } = this.state
    return Boolean(resendCooldownUntil && Date.now() < resendCooldownUntil)
  }

  canResendCode = () => {
    const { loading } = this.state
    const { saving } = this.props

    return !loading && !saving && !this.isResendOnCooldown()
  }

  startResendCooldown = () => {
    const resendCooldownUntil = Date.now() + (RESEND_COOLDOWN_SECONDS * 1000)

    this.setStateIfMounted({
      resendCooldownUntil,
      resendCooldownSecondsRemaining: RESEND_COOLDOWN_SECONDS,
    })

    this.clearResendCooldownTimer()
    this.resendCooldownTimer = setInterval(() => {
      if (!this._isMounted) {
        this.clearResendCooldownTimer()
        return
      }

      const remaining = Math.max(
        0,
        Math.ceil((resendCooldownUntil - Date.now()) / 1000)
      )

      if (remaining === 0) {
        this.clearResendCooldownTimer()
        this.setStateIfMounted({ resendCooldownUntil: null, resendCooldownSecondsRemaining: 0 })
        return
      }

      this.setStateIfMounted({ resendCooldownSecondsRemaining: remaining })
    }, 1000)
  }

  clearResendCooldown = () => {
    this.clearResendCooldownTimer()
    this.setState({
      resendCooldownUntil: null,
      resendCooldownSecondsRemaining: 0,
    })
  }

  openChangePhoneStage = (phoneError = '') => {
    this.clearResendCooldown()
    this.setState({
      stage: PHONE_STAGE,
      newPhoneNumber: this.state.phoneNumber || this.props.phoneNumber || '',
      phoneError: phoneError || '',
      code: '',
      submitEnabled: false,
      verificationStatus: '',
      awaitingSetup: false,
      loading: false,
    }, () => {
      if (this.phoneInput) {
        this.phoneInput.focus()
      }
    })
  }

  loadPhoneNumber = () => {
    const { phoneNumber } = this.props

    if (phoneNumber) {
      this.setState({ phoneNumber: phoneNumber, newPhoneNumber: phoneNumber })
      return
    }

    if (!window.App || !window.App.currentUserId) {
      return
    }

    $.ajax(API.V3.userGet({ id: window.App.currentUserId }))
      .done((response) => {
        const number = (response.data && response.data.attributes && response.data.attributes.phone) || ''
        this.setStateIfMounted({ phoneNumber: number, newPhoneNumber: number })
      })
  }

  contactValidationUrl = () => {
    return coreUtils.phoneVerificationRequired()
      ? '/users/validate_mobile_contact_phone'
      : '/users/validate_contact_phone'
  }

  validateContactPhone = (contactPhone) => {
    return new Promise((resolve) => {
      $.ajax({
        url: this.contactValidationUrl(),
        dataType: 'json',
        data: { user_attributes: { contact_phone: contactPhone } },
      })
        .fail(() => {
          document.location.href = '/500'
          resolve(false)
        })
        .done((result) => {
          if (result.valid) {
            resolve(true)
          } else {
            this.setStateIfMounted({
              phoneError: result.error_message || 'Please enter a valid mobile number',
            })
            resolve(false)
          }
        })
    })
  }

  setupPhoneVerification = (event) => {
    if (event) event.preventDefault()

    const isManualResend = Boolean(event)
    // Initial setup must not bail on parent `saving` — that left awaitingSetup true
    // and rendered an empty white panel (REB-3682).
    if (isManualResend) {
      if (!this.canResendCode()) return
    } else if (this.state.loading || this.isResendOnCooldown()) {
      return
    }

    const request = API.V3.phoneVerificationGenerate()

    request.beforeSend = () => {
      this.setStateIfMounted({
        loading: true,
        verificationStatus: '',
        awaitingSetup: !isManualResend,
      })
    }

    $.ajax(request)
      .done((data) => {
        const status = data.verification_status

        if (status === 'verified') {
          this.handleVerificationStatus(status)
          return
        }

        if (status === 'skipped' || status === 'unavailable') {
          this.handleVerificationStatus(status)
          return
        }

        if (isManualResend) {
          this.startResendCooldown()
          this.setStateIfMounted({
            awaitingSetup: false,
            code: '',
            submitEnabled: false,
          }, () => {
            this.handleVerificationStatus(status)
          })
          return
        }

        this.setStateIfMounted({ awaitingSetup: false }, () => {
          this.handleVerificationStatus(status)
        })
      })
      .fail((xhr) => {
        // Setup returns HTTP 400 when the verification row cannot be created
        // ({ result: 'failed' }) or when status is unavailable. Without a fail
        // handler, awaitingSetup stays true and the panel looks blank white.
        const status = xhr && xhr.responseJSON && xhr.responseJSON.verification_status

        if (status === 'unavailable' || status === 'skipped') {
          this.handleVerificationStatus(status)
          return
        }

        if (coreUtils.phoneVerificationRequired()) {
          this.openChangePhoneStage(SETUP_FAIL_PHONE_ERROR)
          return
        }

        this.setStateIfMounted({
          awaitingSetup: false,
          loading: false,
          verificationStatus: 'failed',
        })
      })
      .always(() => {
        this.setStateIfMounted({ loading: false }, () => {
          if (this.state.stage === CODE_STAGE && !this.state.awaitingSetup && this.codeInput) {
            this.codeInput.focus()
          }
        })
      })
  }

  handleVerificationStatus = (status) => {
    if (status === 'verified') {
      window.App = window.App || {}
      window.App.userPhoneVerified = true
      this.props.onVerified()
      return
    }

    if (status === 'unavailable' || status === 'skipped') {
      window.App = window.App || {}
      window.App.userPhoneIsValid = false
      window.App.userPhoneVerified = false

      if (coreUtils.phoneVerificationRequired()) {
        this.openChangePhoneStage('Please enter a valid mobile number')
        return
      }

      if (this.props.onUnavailable) {
        this.props.onUnavailable()
      }
      this.setStateIfMounted({
        verificationStatus: status,
        loading: false,
        submitEnabled: false,
        awaitingSetup: false,
      })
      return
    }

    this.setStateIfMounted({ verificationStatus: status, loading: false, awaitingSetup: false })
  }

  handleCodeChange = (event) => {
    const { loading } = this.state
    const { saving } = this.props

    if (loading || saving) {
      return
    }

    const code = event.target.value.replace(/\D/g, '').slice(0, this.state.maxCodeLength)

    if (this.props.onClearSubmitError) {
      this.props.onClearSubmitError()
    }

    this.setState(
      {
        code: code,
        submitEnabled: code.length === this.state.maxCodeLength,
        verificationStatus: '',
      },
      () => {
        if (code.length === this.state.maxCodeLength) {
          this.verifyPhone(code)
        }
      }
    )
  }

  verifyPhone = (codeOverride) => {
    const { loading, maxCodeLength } = this.state
    const { saving } = this.props

    if (loading || saving) {
      return
    }

    const code = (codeOverride != null ? codeOverride : this.state.code).replace(/\D/g, '').slice(0, maxCodeLength)

    if (code.length !== maxCodeLength) {
      return
    }

    const request = API.V3.phoneVerificationVerify({ code })

    this.setStateIfMounted({ loading: true })

    $.ajax(request)
      .done((data) => {
        this.handleVerificationStatus(data.verification_status)
      })
      .fail(() => {
        this.setStateIfMounted({
          loading: false,
          verificationStatus: 'failed',
          submitEnabled: false,
        })
      })
      .always(() => {
        this.setStateIfMounted({ loading: false })
      })
  }

  handleContinue = () => {
    if (!this.state.submitEnabled || this.props.saving || this.state.loading) {
      return
    }

    this.verifyPhone()
  }

  handleChangeNumber = () => {
    if (this.state.loading || this.props.saving) {
      return
    }

    this.openChangePhoneStage()
  }

  handleBackToCode = () => {
    if (this.state.loading || this.props.saving) {
      return
    }

    if (this.props.onChangePhoneDismissed) {
      this.props.onChangePhoneDismissed()
    }

    this.setState({
      stage: CODE_STAGE,
      phoneError: '',
      code: '',
      submitEnabled: false,
      verificationStatus: '',
    }, () => {
      if (this.codeInput) {
        this.codeInput.focus()
      }
    })
  }

  handlePhoneChange = (event) => {
    if (this.state.loading || this.props.saving) {
      return
    }

    if (this.props.onClearSubmitError) {
      this.props.onClearSubmitError()
    }

    this.setState({
      newPhoneNumber: event.target.value,
      phoneError: '',
    })
  }

  handlePhoneContinue = () => {
    const userId = window.App && window.App.currentUserId
    const contactPhone = this.state.newPhoneNumber.trim()

    if (!userId) {
      this.setState({ phoneError: 'Please sign in again to continue.' })
      return
    }

    if (!contactPhone) {
      this.setState({ phoneError: 'Please enter your mobile number' })
      return
    }

    this.setState({ loading: true, phoneError: '' })

    this.validateContactPhone(contactPhone).then((valid) => {
      if (!valid) {
        this.setStateIfMounted({ loading: false })
        return
      }

      $.ajax(API.V3.userUpdate(
        { 'contact-phone': contactPhone },
        userId,
        { forcePhoneVerificationReset: true }
      ))
        .done(() => {
          window.App = window.App || {}
          window.App.userPhoneIsValid = true
          window.App.userPhoneVerified = false

          if (this.props.onPhoneUpdated) {
            this.props.onPhoneUpdated(contactPhone)
          }

          this.setStateIfMounted({
            stage: CODE_STAGE,
            phoneNumber: contactPhone,
            newPhoneNumber: contactPhone,
            phoneError: '',
            code: '',
            submitEnabled: false,
            verificationStatus: '',
            loading: false,
          }, () => {
            this.setupPhoneVerification()
          })
        })
        .fail((xhr) => {
          let message = 'Sorry, we could not update your contact number. Please try again.'

          try {
            const json = xhr && xhr.responseJSON
            if (json && json.errors && json.errors[0] && json.errors[0].detail) {
              message = json.errors[0].detail
            }
          } catch (e) {}

          this.setStateIfMounted({ loading: false, phoneError: message })
        })
    })
  }

  renderFooterActions = ({ backLabel, backAction, continueLabel, continueAction, continueEnabled }) => {
    const { saving } = this.props
    const { loading } = this.state
    const isBusy = loading || saving

    return (
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-neutral btn-base-large btn-inline-block text-base-gray-text-secondary"
          onClick={backAction}
          disabled={isBusy}
        >
          <hero-icon-outline
            name="arrow-left"
            class="h-4 w-4 flex-shrink-0 text-base-gray-text-secondary"
          />
          <span className="text-xs sm:text-sm md:text-base font-medium whitespace-nowrap">
            {backLabel}
          </span>
        </button>
        <button
          type="button"
          className='btn-blue-main btn-base-large btn-inline-block'
          disabled={!continueEnabled || isBusy}
          onClick={continueAction}
        >
          <span className="text-xs sm:text-sm md:text-base font-medium whitespace-nowrap">
            {saving ? 'Submitting...' : (loading ? 'Verifying...' : continueLabel)}
          </span>
          <hero-icon-outline
            name="arrow-right"
            class={`h-4 w-4 text-surface flex-shrink-0 ${isBusy ? 'invisible' : ''}`}
          />
        </button>
      </div>
    )
  }

  renderPhoneStageContent = () => {
    const { newPhoneNumber, phoneError, loading } = this.state
    const { submitError } = this.props
    const fieldError = phoneError || submitError
    const hasError = Boolean(fieldError)

    return (
      <div className="pb-0 flex flex-col gap-4 md:gap-6">
        <div className="flex flex-col gap-1 text-center">
          <p className="text-sm font-extrabold tracking-wide text-base-blue-main uppercase">
            Update your mobile number
          </p>
          <h2 className="text-xl font-extrabold text-base-gray-text-main">
            What&apos;s your new number?
          </h2>
          <p className="text-base font-medium text-base-gray-text-main">
            Businesses will use this to get in touch about your job. e.g. 0491 123 456
          </p>
        </div>

        <div className="flex flex-col items-center gap-4 w-full md:max-w-[350px] md:mx-auto">
          <input
            id="dqr-change-phone-number"
            ref={(el) => { this.phoneInput = el }}
            type="tel"
            inputMode="tel"
            className={[
              'w-full h-12 px-3 rounded-5 text-center text-xl font-extrabold text-base-gray-text-main',
              hasError
                ? 'border-2 border-base-red-main focus:border-base-red-main'
                : (newPhoneNumber.length > 0 ? 'border border-base-border' : 'border-2 border-base-blue-main'),
            ].join(' ')}
            placeholder="0412 345 678"
            value={newPhoneNumber}
            autoComplete="tel"
            onChange={this.handlePhoneChange}
            disabled={loading}
          />
          {hasError && (
            <p className="text-sm font-normal text-base-red-900 text-center">{fieldError}</p>
          )}
        </div>
      </div>
    )
  }

  renderCodeStageContent = () => {
    const {
      loading,
      phoneNumber,
      code,
      verificationStatus,
      resendCooldownSecondsRemaining,
    } = this.state
    const { submitError, saving } = this.props
    const isBusy = loading || saving
    const codeError = verificationStatus === 'failed' ? 'Wrong code. Try again.' : ''
    const hasError = Boolean(codeError)
    const showSetupSpinner = this.state.awaitingSetup
    const resendButtonLabel = resendCooldownSecondsRemaining > 0
      ? `Send again in ${resendCooldownSecondsRemaining}s`
      : 'Send it again'

    if (showSetupSpinner) {
      return (
        <div className="py-8 flex flex-col items-center gap-3" role="status" aria-live="polite">
          <div className="spinner-icon text-center" aria-hidden="true" />
          <p className="text-base font-medium text-base-gray-text-main text-center">
            Sending a code to your mobile...
          </p>
        </div>
      )
    }

    const hasCode = code.length > 0

    return (
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="flex flex-col gap-1 text-center">
          <p className="text-sm font-extrabold tracking-wide text-base-blue-main uppercase">
            One-Time Password (OTP)
          </p>
          <div className="text-base font-medium text-base-gray-text-main">
            <p>
              We&apos;ve sent a code to{' '}
              {phoneNumber ? (
                <span className="font-extrabold">{phoneNumber}</span>
              ) : (
                'your mobile'
              )}
              .
            </p>
            <p>Enter the 4 digits below.</p>
          </div>
        </div>

        {submitError && (
          <div className="w-full md:max-w-[350px] md:mx-auto">
            <DangerAlert message={submitError} />
          </div>
        )}

        <div className="flex flex-col items-center w-full md:max-w-[350px] md:mx-auto">
          <input
            id="dqr-phone-otp-code"
            ref={(el) => { this.codeInput = el }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={this.state.maxCodeLength}
            className={[
              'input-text-base w-full h-12 px-3 rounded-5 text-center bg-surface',
              hasError
                ? 'border-2 border-base-red-main focus:border-base-red-main text-sm font-normal text-base-gray-text-main'
                : (hasCode
                  ? 'border border-base-border text-base font-extrabold text-base-gray-text-main'
                  : 'border-2 border-base-blue-main text-sm font-normal text-base-gray-text-main'),
            ].join(' ')}
            placeholder="1234"
            value={code}
            autoComplete="one-time-code"
            onChange={this.handleCodeChange}
            disabled={isBusy}
            aria-busy={isBusy}
            aria-label="Enter code"
          />
          {hasError && (
            <p className="mt-1 text-sm font-normal text-base-red-900 text-center">{codeError}</p>
          )}
        </div>

        <p className="py-2 text-center text-base font-medium text-base-gray-text-main">
          Didn&apos;t receive a code?{' '}
          <button
            type="button"
            className={[
              'font-extrabold underline',
              this.canResendCode() ? 'text-base-link' : 'text-base-gray-text-secondary cursor-not-allowed',
            ].join(' ')}
            onClick={this.setupPhoneVerification}
            disabled={!this.canResendCode()}
          >
            {loading && resendCooldownSecondsRemaining === 0 ? 'Sending...' : resendButtonLabel}
          </button>
        </p>

        {this.renderFooterActions({
          backLabel: 'Change number',
          backAction: this.handleChangeNumber,
          continueLabel: 'Continue',
          continueAction: this.handleContinue,
          continueEnabled: this.state.submitEnabled,
        })}
      </div>
    )
  }

  render() {
    const { onClose } = this.props
    const { stage, newPhoneNumber, awaitingSetup } = this.state
    const isCodeStage = stage === CODE_STAGE
    const showCodeSpinner = isCodeStage && awaitingSetup

    return (
      <div className="dqr-otp-verification flex flex-col gap-4 md:gap-6 max-md:min-h-0 max-md:flex-1 max-md:h-full md:h-auto md:flex-none">
        {onClose && (
          <div className="hidden md:flex shrink-0 justify-end">
            <WizardCloseButton compact onClick={onClose} />
          </div>
        )}
        <div className={[
          'dqr-otp-verification__content',
          isCodeStage && !showCodeSpinner
            ? 'flex flex-col'
            : 'max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto max-md:overflow-x-hidden',
        ].join(' ')}
        >
          {stage === PHONE_STAGE ? this.renderPhoneStageContent() : this.renderCodeStageContent()}
        </div>

        {stage === PHONE_STAGE && (
          <div className="shrink-0 max-md:mt-auto pt-2 md:pt-6">
            {this.renderFooterActions({
              backLabel: 'Back',
              backAction: this.handleBackToCode,
              continueLabel: 'Continue',
              continueAction: this.handlePhoneContinue,
              continueEnabled: newPhoneNumber.trim().length > 0,
            })}
          </div>
        )}
      </div>
    )
  }
}
